import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

// API Data Types
export type AnalyticsTimeframe =
  | "15m" // Last 15 minutes
  | "30m" // Last 30 minutes
  | "1h" // Last hour
  | "3h" // Last 3 hours
  | "6h" // Last 6 hours
  | "12h" // Last 12 hours
  | "24h" // Last 24 hours
  | "3d" // Last 3 days
  | "7d" // Last 7 days
  | "30d" // Last 30 days
  | "custom"; // Custom date range

export type AgentAnalyticsMetrics = {
  // Observability Metrics (System Performance & Health)
  avgFirstTtsLatencyMs: number; // Average time to first TTS audio frame per turn
  avgSttLatencyMs: number; // Average time difference (in ms) between the final transcription frame (before endpointing) and the text event entering the handler
  avgTurnAroundTimeMs: number;
  errorRate: number; // 0-100 percentage

  // Product/User Experience Metrics
  avgSessionLengthMinutes: number;
  transferRate: number; // 0-100 percentage
  avgTurnDepth: number; // Average turns per session
};

export type AgentAnalyticsTimePoint = {
  timestamp: string;
  // Sessions data
  sessions: {
    count: number;
  };
  // TTS latency data with percentiles
  ttsLatency: {
    avg: number;
    p50: number;
    p95: number;
  };
  // STT latency data with percentiles
  sttLatency: {
    avg: number;
    p50: number;
    p95: number;
  };
  // First response time with percentiles
  firstResponseTime: {
    avg: number;
    p50: number;
    p95: number;
  };
  // Turn around time with percentiles
  turnAroundTime: {
    avg: number;
    p50: number;
    p95: number;
  };
  // Session length with percentiles
  sessionLength: {
    avg: number;
    p50: number;
    p95: number;
  };
  // Error rate
  errorRate: number;
  // Transfer rate
  transferRate: number;
  // Turn depth data
  turnDepth: {
    avg: number;
    p50: number;
    p95: number;
  };
};

export type AgentAnalyticsData = {
  agentId: string;
  startDate: string;
  endDate: string;
  timeframe: AnalyticsTimeframe;
  totalSessions: number;
  metrics: AgentAnalyticsMetrics;
  // Aggregated time points with percentiles
  timePoints: AgentAnalyticsTimePoint[];
};

export type AgentAnalyticsParams = {
  agentId: string;
  timeframe: AnalyticsTimeframe;
  startDate?: number; // Epoch milliseconds (UTC) - required for timeframe: "custom"
  endDate?: number; // Epoch milliseconds (UTC) - required for timeframe: "custom"
  timezone?: string; // User's timezone for daily bucket grouping (e.g., "America/New_York")
};

export type AgentAnalyticsState = {
  currentAnalytics: AgentAnalyticsData | null;
  isLoading: boolean;
  error: string | null;
};

// API Endpoint: GET /v1/agents/:agentId/analytics
// Query params: timeframe, startDate?, endDate?
// Response: { data: AgentAnalyticsData }
// Note: All timestamps are in UTC. Frontend handles timezone conversion for display.
export const getAgentAnalytics = createAsyncThunk("agentAnalytics/getAnalytics", async (params: AgentAnalyticsParams) => {
  const queryParams = new URLSearchParams({
    timeframe: params.timeframe,
  });

  // Add date params for custom timeframe (epoch milliseconds)
  if (params.timeframe === "custom") {
    if (!params.startDate || !params.endDate) {
      throw new Error("startDate and endDate are required for custom timeframe");
    }
    queryParams.append("startDate", params.startDate.toString());
    queryParams.append("endDate", params.endDate.toString());
  }

  // Add timezone for daily bucket grouping (to match sessions page display)
  if (params.timezone) {
    queryParams.append("timezone", params.timezone);
  }

  const { data } = await makeRequest<{ data: AgentAnalyticsData }>(`/v1/agents/${params.agentId}/analytics?${queryParams}`);
  return data;
});

const initialState: AgentAnalyticsState = {
  currentAnalytics: null,
  isLoading: false,
  error: null,
};

const agentAnalyticsSlice = createSlice({
  name: "agentAnalytics",
  initialState,
  reducers: {
    clearAnalytics: (state) => {
      state.currentAnalytics = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAgentAnalytics.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getAgentAnalytics.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentAnalytics = action.payload;
      state.error = null;
    });
    builder.addCase(getAgentAnalytics.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch agent analytics";
    });
  },
});

export const { clearAnalytics } = agentAnalyticsSlice.actions;
export const agentAnalyticsReducer = agentAnalyticsSlice.reducer;
